import { useState, useRef, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Combobox } from "@/components/common/Combobox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { apiErrorMessage } from "@/lib/api";
import { fmtCurrency } from "@/lib/format";
import dayjs from "dayjs";
import { toast } from "sonner";
import {
  UploadCloud, FileText, Loader2, ShieldCheck, AlertTriangle, CheckCircle2, ShieldAlert,
  Building2, PackagePlus, X, TrendingUp, TrendingDown, Minus, ScanText, Receipt, Plus,
} from "lucide-react";

const num = (v) => (v === "" || v == null ? 0 : Number(v));
const fyFromDate = (d) => {
  if (!d) return "";
  const dt = new Date(d); if (isNaN(dt)) return "";
  const s = dt.getMonth() >= 3 ? dt.getFullYear() : dt.getFullYear() - 1;
  return `${s}-${String(s + 1).slice(-2)}`;
};
const MIME = { pdf: "application/pdf", jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png" };
const toB64 = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(",")[1]); r.onerror = rej; r.readAsDataURL(file); });

function Chip({ tone, children, testId }) {
  const cls = { green: "border-emerald-200 bg-emerald-50 text-emerald-700", amber: "border-amber-200 bg-amber-50 text-amber-800", rose: "border-rose-200 bg-rose-50 text-rose-700", stone: "border-stone-200 bg-stone-100 text-stone-600" }[tone];
  return <span data-testid={testId} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}>{children}</span>;
}

function PriceDelta({ cmp }) {
  if (!cmp || cmp.direction === "NONE") return null;
  if (cmp.direction === "NEW") return <div className="text-[11px] text-stone-500">New buyer/product price — no history</div>;
  if (cmp.direction === "SAME") return <div className="flex items-center gap-1 text-[11px] text-emerald-700"><Minus className="h-3 w-3" /> Same as previous ({fmtCurrency(cmp.previous)})</div>;
  const up = cmp.direction === "UP";
  return (
    <div className={`flex flex-wrap items-center gap-x-2 text-[11px] ${up ? "text-amber-700" : "text-sky-700"}`}>
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      <strong>Buyer price {up ? "increased" : "reduced"}</strong>
      <span>{fmtCurrency(cmp.previous)} → {fmtCurrency(cmp.current)}</span>
      <span className="font-mono">({cmp.delta > 0 ? "+" : ""}{cmp.delta} · {cmp.deltaPct > 0 ? "+" : ""}{cmp.deltaPct}%)</span>
    </div>
  );
}

function CreateProductDialog({ open, onClose, initialName, onCreated }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ name: initialName || "", productType: "", sku: "", category: "" }); }, [open, initialName]);
  const save = async () => {
    if (!form.name?.trim()) return toast.error("Product name required");
    if (!form.productType) return toast.error("Select Bakery or Non-Bakery");
    setBusy(true);
    try {
      const r = await api.post("/products", { name: form.name.trim(), productType: form.productType, sku: form.sku?.trim() || "", category: form.category?.trim() || "", active: true, hasRecipe: false, classificationRequired: false, classificationSource: "USER", source: "TALLY_IMPORT" });
      const p = r.data || {};
      onCreated({ id: p.id || p._id, productId: p.productId, name: p.name || form.name, sku: p.sku || form.sku, productType: form.productType });
      toast.success(`Product created (${p.productId || ""})`); onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Could not create product")); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white sm:max-w-lg" data-testid="tally-create-product">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><PackagePlus className="h-4 w-4 text-amber-700" /> Create New Product</DialogTitle>
          <DialogDescription className="text-xs">Prefilled from the invoice. Classification is mandatory. Created only on your action.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-1">
          <div className="col-span-2 space-y-1.5"><Label className="text-xs">Product Name</Label><Input data-testid="tally-np-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Classification <span className="text-rose-500">*</span></Label>
            <Select value={form.productType || ""} onValueChange={(v) => setForm({ ...form, productType: v })}>
              <SelectTrigger data-testid="tally-np-classification" className={`bg-white ${!form.productType ? "border-amber-400" : ""}`}><SelectValue placeholder="Bakery / Non-Bakery" /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="BAKERY">Bakery</SelectItem><SelectItem value="NON_BAKERY">Non-Bakery</SelectItem></SelectContent>
            </Select></div>
          <div className="space-y-1.5"><Label className="text-xs">SKU (optional)</Label><Input data-testid="tally-np-sku" value={form.sku || ""} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="tally-np-save" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={save}>{busy && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TallyUpload() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const { data: buyerData, refetch: refetchBuyers } = useResource("/buyers");
  const { data: prodData, refetch: refetchProducts } = useResource("/products");
  const { data: companyData } = useResource("/settings/company");
  const { refetch: refetchBills } = useResource("/sales-bills");
  const buyers = Array.isArray(buyerData) ? buyerData : [];
  const products = Array.isArray(prodData) ? prodData : [];
  const tolerance = num(companyData?.salesRoundingTolerance) || 10;

  const [file, setFile] = useState(null);
  const [docB64, setDocB64] = useState("");
  const [working, setWorking] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [buyerHdr, setBuyerHdr] = useState(null);   // {extracted fields, matchStatus, matchedId, matchedName}
  const [buyerId, setBuyerId] = useState("");        // resolved buyer uuid
  const [invNo, setInvNo] = useState("");
  const [invDate, setInvDate] = useState("");
  const [docTotals, setDocTotals] = useState(null);
  const [lines, setLines] = useState([]);
  const [createDlg, setCreateDlg] = useState(-1);
  const [booking, setBooking] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [buyerMode, setBuyerMode] = useState("pick"); // pick | new-branch | new-buyer

  const reset = () => { setExtracted(null); setFile(null); setLines([]); setBuyerId(""); setInvNo(""); setInvDate(""); setDocTotals(null); setBuyerHdr(null); setDocB64(""); setBuyerMode("pick"); };

  const onExtract = async () => {
    if (!file) return;
    const ext = (file.name || "").split(".").pop()?.toLowerCase() || "pdf";
    setWorking(true); setExtracted(null);
    try {
      const b64 = await toB64(file); setDocB64(b64);
      const res = await api.post("/sales-bills/tally/extract", { filename: file.name, contentBase64: b64, mimeType: MIME[ext] || "application/pdf" });
      const d = res.data || {};
      setExtracted(d);
      const b = d.buyer || {};
      setBuyerHdr(b);
      setBuyerId(b.matchedId || "");
      setInvNo(d.invoice?.invoiceNumber || "");
      setInvDate(d.invoice?.invoiceDate || "");
      setDocTotals(d.documentTotals || null);
      setLines((d.lines || []).map((l) => ({
        extractedName: l.extractedName, sku: l.sku, hsn: l.hsn,
        quantity: l.quantity ?? "", unit: l.unit || "pcs", rate: l.rate ?? "",
        discount: l.discount ?? 0, cgstPct: l.cgstPct ?? 0, sgstPct: l.sgstPct ?? 0, igstPct: l.igstPct ?? 0,
        resolution: l.matchStatus === "MATCHED" ? "MATCHED" : "REVIEW",
        productId: l.matchStatus === "MATCHED" ? l.matchedId : "", productCode: l.matchStatus === "MATCHED" ? l.matchedCode : "",
        matchedName: l.matchStatus === "MATCHED" ? l.matchedName : "", suggestedId: l.suggestedId || "",
        classificationRequired: false, productType: "", priceComparison: l.priceComparison || null,
      })));
      refetchBuyers(); refetchProducts();
      if (d.duplicate) {
        toast.error("This Tally bill has already been imported.");
      } else {
        toast.success("Invoice extracted — resolve buyer & lines, verify value, then book");
      }
    } catch (e) { toast.error(apiErrorMessage(e, "Extraction failed")); } finally { setWorking(false); }
  };

  const setLine = (i, patch) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const matchExisting = (i, id) => { const p = products.find((x) => x.id === id); setLine(i, { resolution: "MATCHED", productId: id, productCode: p?.productId || "", matchedName: p?.name || "", classificationRequired: !!p?.classificationRequired }); };
  const acceptSuggestion = (i) => { const l = lines[i]; const p = products.find((x) => x.id === l.suggestedId); setLine(i, { resolution: "MATCHED", productId: l.suggestedId, productCode: p?.productId || "", matchedName: p?.name || "", classificationRequired: !!p?.classificationRequired }); };
  const onProductCreated = (i, p) => { refetchProducts(); setLine(i, { resolution: "NEW_PRODUCT", productId: p.id, productCode: p.productId, matchedName: p.name, classificationRequired: false, productType: p.productType }); };
  const saveClassification = async (i, cls) => { const l = lines[i]; try { await api.put(`/products/${l.productId}`, { productType: cls, classificationRequired: false }); setLine(i, { classificationRequired: false, productType: cls }); refetchProducts(); toast.success("Classified"); } catch (e) { toast.error(apiErrorMessage(e, "Failed")); } };

  const buyer = buyers.find((b) => b.id === buyerId);
  const buyerStatus = buyerHdr?.matchStatus;
  const gstinFromDoc = buyerHdr?.gstin || "";
  const branchCandidates = buyerHdr?.matchMeta?.candidates || [];
  const isMultiBranch = buyerStatus === "MULTI_BRANCH";

  const active = lines.filter((l) => l.resolution !== "IGNORED");
  const bookable = active.filter((l) => l.resolution === "MATCHED" || l.resolution === "NEW_PRODUCT");
  const unresolved = active.filter((l) => l.resolution === "REVIEW").length;
  const pendingClass = bookable.filter((l) => l.classificationRequired).length;
  const duplicate = extracted?.duplicate || null;

  const sysTotals = useMemo(() => {
    let sub = 0, disc = 0, tax = 0, grand = 0;
    bookable.forEach((l) => {
      const gross = num(l.quantity) * num(l.rate);
      const d = gross * num(l.discount) / 100;
      const taxable = gross - d;
      const g = taxable * (num(l.cgstPct) + num(l.sgstPct) + num(l.igstPct)) / 100;
      sub += gross; disc += d; tax += g; grand += taxable + g;
    });
    const freight = num(docTotals?.freight);
    return { subtotal: sub, discount: disc, tax, freight, grandTotal: grand + freight };
  }, [bookable, docTotals]);

  const docGrand = num(docTotals?.grandTotal);
  const hasDoc = docGrand > 0;
  const valueDiff = hasDoc ? Math.round((docGrand - sysTotals.grandTotal) * 100) / 100 : 0;
  const valueMatched = !hasDoc || Math.abs(valueDiff) <= tolerance;

  const buyerResolved = buyerMode === "new-buyer"
    ? true
    : buyerMode === "new-branch"
      ? true
      : (!!buyerId || buyerStatus === "NEW_BUYER");
  const dateValid = invDate && dayjs(invDate).isValid();
  const isBackdated = dateValid && dayjs(invDate).isBefore(dayjs().format("YYYY-MM-DD"), "day");

  const canBook = !booking && buyerResolved && !!invNo && dateValid && bookable.length > 0 && unresolved === 0 && pendingClass === 0 && valueMatched;

  const book = async () => {
    setConfirmOpen(false); setBooking(true);
    let buyerPayload;
    let explicitBuyerId;
    if (buyerMode === "new-branch") {
      // create a new store/branch under the matched parent, sharing the GSTIN
      const parent = branchCandidates[0] || {};
      buyerPayload = { ...buyerHdr, gstin: gstinFromDoc, parentCompanyId: parent.parentCompanyId || parent.id, parentCompany: parent.parentCompany || parent.name };
    } else if (buyerMode === "new-buyer") {
      buyerPayload = { ...buyerHdr, gstin: gstinFromDoc, createNew: true };
    } else if (buyerId) {
      explicitBuyerId = buyerId;
      buyerPayload = { gstin: buyer?.gstin || gstinFromDoc, name: buyer?.name || buyerHdr?.name };
    } else if (buyerStatus === "NEW_BUYER") {
      buyerPayload = { ...buyerHdr, gstin: gstinFromDoc };
    } else {
      buyerPayload = { gstin: gstinFromDoc, name: buyerHdr?.name };
    }
    const payload = {
      externalInvoiceNumber: invNo, invoiceDate: invDate,
      buyer: buyerPayload, buyerId: explicitBuyerId, documentTotals: docTotals || undefined,
      documentFilename: file?.name || "tally-invoice",
      lines: active.map((l) => {
        const p = products.find((x) => x.id === l.productId);
        return { resolution: l.resolution, productCode: l.productCode, productName: p?.name || l.matchedName || l.extractedName, matched: l.resolution !== "REVIEW",
          quantity: num(l.quantity), unit: l.unit, rate: num(l.rate), discount: num(l.discount), hsn: l.hsn,
          cgstPct: num(l.cgstPct), sgstPct: num(l.sgstPct), igstPct: num(l.igstPct) };
      }),
    };
    try {
      const res = await api.post("/sales-bills/tally/book", payload);
      toast.success(`Sales bill ${res.data?.billNo || ""} booked (source: TALLY IMPORT)`);
      reset(); refetchBills(); refetchBuyers();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "TALLY_BILL_ALREADY_IMPORTED") {
        setExtracted((prev) => ({ ...(prev || {}), duplicate: {
          code: detail.code, billNo: detail.billNo, invoiceDate: detail.invoiceDate,
          status: detail.status, existingBillId: detail.existingBillId,
          buyerName: buyerHdr?.name, financialYear: fyFromDate(invDate),
        } }));
        toast.error("This Tally bill has already been imported.");
      } else {
        toast.error(apiErrorMessage(e, detail?.code ? `${detail.code.replace(/_/g, " ")}` : "Booking failed"));
      }
    } finally { setBooking(false); }
  };

  const RES_CHIP = {
    MATCHED: <Chip tone="green">✓ Matched</Chip>, NEW_PRODUCT: <Chip tone="amber"><PackagePlus className="h-3 w-3" /> New Product</Chip>,
    REVIEW: <Chip tone="amber">⚠ Review</Chip>, IGNORED: <Chip tone="stone">Ignored</Chip>,
  };
  const buyerChip = buyerStatus === "MATCHED_GSTIN" ? <Chip tone="green" testId="tally-buyer-status">✓ GSTIN Matched</Chip>
    : buyerStatus === "NEW_BUYER" ? <Chip tone="amber" testId="tally-buyer-status"><Plus className="h-3 w-3" /> New Buyer</Chip>
    : buyerStatus === "MULTI_BRANCH" ? <Chip tone="amber" testId="tally-buyer-status"><Building2 className="h-3 w-3" /> Select Branch</Chip>
    : <Chip tone="rose" testId="tally-buyer-status">⚠ Buyer Review Required</Chip>;

  return (
    <div className="space-y-6">
      <PageHeader title="Customer / Tally Bill Import" subtitle="Import customer/Tally invoices to create operational sales records, including backdated invoices." />

      <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50/70 px-4 py-3 text-sm text-emerald-900">
        <Receipt className="mt-0.5 h-4 w-4 shrink-0" />
        <span><strong>Operational sales import.</strong> Booking creates a real Sales Bill (source = TALLY_IMPORT) using the original invoice date. Buyer is matched by <strong>GSTIN</strong>; rates are captured as buyer prices. Backdated invoices are supported and do <strong>NOT</strong> deduct current finished-goods stock.</span>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
        <div data-testid="tally-dropzone" onClick={() => inputRef.current?.click()}
          className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-stone-300 bg-stone-50/50 py-10 text-center hover:border-amber-400 hover:bg-amber-50/40">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-700"><ScanText className="h-6 w-6" /></div>
          {file ? <div className="flex items-center gap-2 text-sm font-medium text-stone-800"><FileText className="h-4 w-4 text-amber-700" /> {file.name}</div>
            : <><div className="font-display text-base font-semibold text-stone-800">Select a customer / Tally invoice</div><div className="mt-1 text-xs text-muted-foreground">PDF · JPG · PNG</div></>}
          <input ref={inputRef} data-testid="tally-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </div>
        <div className="mt-4 flex justify-end">
          <Button data-testid="tally-extract" className="gap-2 bg-amber-700 hover:bg-amber-800" disabled={!file || working} onClick={onExtract}>
            {working ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Extract & Review
          </Button>
        </div>
      </div>

      {extracted && duplicate && (
        <div className="space-y-4 rounded-xl border-2 border-rose-300 bg-rose-50 p-6 shadow-sm" data-testid="tally-duplicate-block">
          <div className="flex items-center gap-2 text-lg font-semibold text-rose-800">
            <AlertTriangle className="h-6 w-6" /> ⚠ Bill Already Imported
          </div>
          <p className="text-sm text-rose-700">This Tally bill has already been imported into Audit Hub and cannot be imported again.</p>
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 rounded-lg border border-rose-200 bg-white p-4 text-sm sm:grid-cols-3">
            <div><div className="text-[10px] font-bold uppercase text-stone-400">Buyer</div><div className="font-medium text-stone-800">{duplicate.buyerName || buyerHdr?.name}</div></div>
            <div><div className="text-[10px] font-bold uppercase text-stone-400">Tally Bill No</div><div className="font-mono font-medium text-stone-800" data-testid="tally-dup-billno">{duplicate.billNo}</div></div>
            <div><div className="text-[10px] font-bold uppercase text-stone-400">Invoice Date</div><div className="font-medium text-stone-800">{duplicate.invoiceDate}</div></div>
            <div><div className="text-[10px] font-bold uppercase text-stone-400">Financial Year</div><div className="font-medium text-stone-800">{duplicate.financialYear}</div></div>
            <div><div className="text-[10px] font-bold uppercase text-stone-400">Existing Bill</div><div className="font-mono font-medium text-stone-800">{duplicate.billNo}</div></div>
            <div><div className="text-[10px] font-bold uppercase text-stone-400">Status</div><div className="font-medium text-emerald-700">{duplicate.status}</div></div>
          </div>
          <div className="flex gap-2">
            <Button data-testid="tally-view-existing" className="gap-2 bg-amber-700 hover:bg-amber-800" onClick={() => navigate(`/sales-bills?highlight=${duplicate.existingBillId}`)}>
              <FileText className="h-4 w-4" /> View Existing Bill
            </Button>
            <Button variant="outline" data-testid="tally-dup-reset" onClick={reset}>Upload a different bill</Button>
          </div>
        </div>
      )}

      {extracted && !duplicate && (
        <div className="space-y-4 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="tally-review">
          <h3 className="font-display text-base font-semibold text-stone-900">Customer / Tally Bill — Review &amp; Confirm</h3>

          {/* buyer + invoice header */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-stone-200 bg-stone-50/70 px-4 py-3" data-testid="tally-buyer-banner">
            <div className="flex items-center gap-2.5">
              <Building2 className="h-4 w-4 text-stone-400" />
              <div>
                <div className="font-display text-sm font-bold uppercase text-stone-900">{buyer?.name || buyerHdr?.name || "Unknown buyer"}</div>
                <div className="font-mono text-xs text-stone-500">{buyer?.gstin || gstinFromDoc || "no GSTIN"}{buyer?.buyerId ? ` · ${buyer.buyerId}` : ""}</div>
              </div>
            </div>
            {buyerChip}
          </div>

          {isMultiBranch && (
            <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50/50 p-3" data-testid="tally-multibranch">
              <Label className="text-xs font-semibold text-amber-900">
                GSTIN {gstinFromDoc} belongs to {branchCandidates.length} branches. Pick the correct store/branch, or create a new one.
              </Label>
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant={buyerMode === "pick" ? "default" : "outline"}
                  className={buyerMode === "pick" ? "bg-amber-700 hover:bg-amber-800" : ""}
                  data-testid="tally-branch-mode-pick" onClick={() => { setBuyerMode("pick"); }}>Select existing branch</Button>
                <Button type="button" size="sm" variant={buyerMode === "new-branch" ? "default" : "outline"}
                  className={buyerMode === "new-branch" ? "bg-amber-700 hover:bg-amber-800" : ""}
                  data-testid="tally-branch-mode-new" onClick={() => { setBuyerMode("new-branch"); setBuyerId(""); }}>
                  <Plus className="h-3 w-3" /> Create new branch</Button>
                <Button type="button" size="sm" variant={buyerMode === "new-buyer" ? "default" : "outline"}
                  className={buyerMode === "new-buyer" ? "bg-amber-700 hover:bg-amber-800" : ""}
                  data-testid="tally-branch-mode-newbuyer" onClick={() => { setBuyerMode("new-buyer"); setBuyerId(""); }}>
                  <Plus className="h-3 w-3" /> Create new buyer</Button>
              </div>
              {buyerMode === "pick" && (
                <Select value={buyerId} onValueChange={setBuyerId}>
                  <SelectTrigger data-testid="tally-branch-select" className="h-9 max-w-md text-sm"><SelectValue placeholder="Select the correct store / branch" /></SelectTrigger>
                  <SelectContent className="max-h-60 bg-white">
                    {branchCandidates.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.buyerId}{c.city ? ` · ${c.city}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {buyerMode === "new-branch" && (
                <div className="text-xs text-amber-900" data-testid="tally-branch-newinfo">
                  A new branch <strong>{buyerHdr?.name}</strong> will be created under <strong>{branchCandidates[0]?.parentCompany || branchCandidates[0]?.name}</strong>, sharing GSTIN {gstinFromDoc}. New Buyer ID auto-generated.
                </div>
              )}
              {buyerMode === "new-buyer" && (
                <div className="text-xs text-amber-900" data-testid="tally-branch-newbuyerinfo">
                  A brand-new independent buyer <strong>{buyerHdr?.name}</strong> (GSTIN {gstinFromDoc}) will be created — its own parent company.
                </div>
              )}
            </div>
          )}

          {(buyerStatus === "REVIEW" || buyerStatus === "INVALID_GSTIN") && (
            <div className="space-y-1.5">
              <Label className="text-xs text-rose-700">{buyerStatus === "INVALID_GSTIN" ? "Invalid GSTIN on the document." : "GSTIN not found / name conflict — select the correct buyer."}</Label>
              <Select value={buyerId} onValueChange={setBuyerId}>
                <SelectTrigger data-testid="tally-buyer-manual" className="h-9 max-w-md text-sm"><SelectValue placeholder="Match buyer manually" /></SelectTrigger>
                <SelectContent className="max-h-60 bg-white">{buyers.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} · {b.buyerId}{b.gstin ? ` · ${b.gstin}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {buyerStatus === "NEW_BUYER" && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 text-xs text-amber-900" data-testid="tally-newbuyer">
              A new buyer will be created on booking from the invoice: <strong>{buyerHdr?.name}</strong> · GSTIN {gstinFromDoc}{buyerHdr?.billingAddress ? ` · ${buyerHdr.billingAddress}` : ""}. Permanent Buyer ID auto-generated.
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-1.5"><Label>Invoice No</Label><Input data-testid="tally-invno" value={invNo} onChange={(e) => setInvNo(e.target.value)} className="h-9 text-sm" />
              {extracted?.invoice?.numberSource === "FILENAME" && <p className="text-[10px] text-amber-700" data-testid="tally-invno-filename">Invoice No. was blank on the bill — taken from the file name. Please verify.</p>}
              {extracted?.invoice?.numberSource === "OCR_SUSPECT" && <p className="text-[10px] text-rose-600" data-testid="tally-invno-suspect">Invoice number could not be read reliably — please type the exact Tally voucher number.</p>}
            </div>
            <div className="space-y-1.5"><Label>Invoice Date {isBackdated && <span className="text-[10px] text-amber-700">(backdated)</span>}</Label><Input data-testid="tally-invdate" type="date" value={invDate} onChange={(e) => setInvDate(e.target.value)} className="h-9 text-sm" /></div>
            <div className="space-y-1.5"><Label>Source</Label><Input value="TALLY_IMPORT" disabled className="h-9 text-sm" /></div>
          </div>

          {/* lines */}
          <div className="space-y-3">
            {lines.map((l, i) => {
              const ignored = l.resolution === "IGNORED";
              const gst = num(l.cgstPct) + num(l.sgstPct) + num(l.igstPct);
              const lineTotal = num(l.quantity) * num(l.rate) * (1 - num(l.discount) / 100) * (1 + gst / 100);
              const needsClass = (l.resolution === "MATCHED" || l.resolution === "NEW_PRODUCT") && l.classificationRequired;
              return (
                <div key={i} data-testid={`tally-line-${i}`} className={`rounded-lg border p-3 ${ignored ? "border-stone-200 bg-stone-50/60 opacity-70" : l.resolution === "REVIEW" ? "border-amber-300 bg-amber-50/40" : "border-stone-200 bg-white"}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-[180px] flex-1">
                      <div className="font-medium text-stone-800">{l.extractedName}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-stone-500">
                        <span>Qty</span><Input data-testid={`tally-qty-${i}`} type="number" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} className="h-7 w-20 text-right" disabled={ignored} />
                        <span>{l.unit}</span>
                        <span>Rate</span><Input data-testid={`tally-rate-${i}`} type="number" value={l.rate} onChange={(e) => setLine(i, { rate: e.target.value })} className="h-7 w-24 text-right" disabled={ignored} />
                        <span>GST {gst}%</span>
                        <span className="font-mono text-stone-700">= {fmtCurrency(lineTotal)}</span>
                      </div>
                    </div>
                    <div data-testid={`tally-line-status-${i}`}>{RES_CHIP[l.resolution]}</div>
                  </div>

                  {!ignored && (l.resolution === "MATCHED" || l.resolution === "NEW_PRODUCT") && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-stone-100 pt-2">
                      <span className="text-xs text-stone-500">Product:</span><span className="text-sm font-medium text-stone-800">{l.matchedName}</span>
                      <span className="font-mono text-[11px] text-stone-400">{l.productCode}</span>
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" data-testid={`tally-change-${i}`} onClick={() => setLine(i, { resolution: "REVIEW", productId: "", productCode: "", matchedName: "" })}>Change</Button>
                      <Button size="sm" variant="ghost" className="ml-auto h-6 px-2 text-[11px] text-stone-500" data-testid={`tally-ignore-${i}`} onClick={() => setLine(i, { resolution: "IGNORED" })}><X className="h-3 w-3" /> Ignore</Button>
                    </div>
                  )}
                  {!ignored && l.resolution === "REVIEW" && (
                    <div className="mt-2 space-y-2 border-t border-amber-200 pt-2">
                      <div className="text-xs font-semibold text-amber-800">⚠ Resolve this line:</div>
                      {l.suggestedId && (
                        <div className="flex items-center gap-2 text-xs" data-testid={`tally-suggest-${i}`}>
                          <span className="text-amber-700">Suggested match available</span>
                          <Button size="sm" variant="outline" className="h-7 bg-amber-700 text-[11px] text-white hover:bg-amber-800" data-testid={`tally-accept-${i}`} onClick={() => acceptSuggestion(i)}>Accept</Button>
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="w-80" data-testid={`tally-prod-wrap-${i}`}>
                          <Combobox
                            testId={`tally-prod-${i}`}
                            value={l.productId || l.suggestedId || ""}
                            onChange={(v) => matchExisting(i, v)}
                            placeholder="Match existing product…"
                            options={products.map((p) => ({ value: p.id, label: p.name, hint: p.productId }))}
                          />
                        </div>
                        <Button size="sm" variant="outline" className="h-8 gap-1 text-[11px]" data-testid={`tally-createnew-${i}`} onClick={() => setCreateDlg(i)}><PackagePlus className="h-3 w-3" /> Create New Product</Button>
                        <Button size="sm" variant="ghost" className="h-8 px-2 text-[11px] text-stone-500" data-testid={`tally-ignore-${i}`} onClick={() => setLine(i, { resolution: "IGNORED" })}><X className="h-3 w-3" /> Ignore</Button>
                      </div>
                    </div>
                  )}
                  {ignored && <div className="mt-2 border-t border-stone-100 pt-2 text-right"><Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" data-testid={`tally-restore-${i}`} onClick={() => setLine(i, { resolution: l.productId ? "MATCHED" : "REVIEW" })}>Restore</Button></div>}
                  {!ignored && needsClass && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 rounded border border-amber-300 bg-amber-50 px-2 py-1.5">
                      <span className="text-[11px] font-semibold text-amber-800">⚠ Classification required:</span>
                      <Select value={l.productType || ""} onValueChange={(v) => saveClassification(i, v)}>
                        <SelectTrigger data-testid={`tally-classification-${i}`} className="h-7 w-40 border-amber-400 bg-white text-xs"><SelectValue placeholder="Bakery / Non-Bakery" /></SelectTrigger>
                        <SelectContent className="bg-white"><SelectItem value="BAKERY">Bakery</SelectItem><SelectItem value="NON_BAKERY">Non-Bakery</SelectItem></SelectContent>
                      </Select>
                    </div>
                  )}
                  {!ignored && (l.resolution === "MATCHED" || l.resolution === "NEW_PRODUCT") && <div className="mt-2" data-testid={`tally-line-price-${i}`}><PriceDelta cmp={l.priceComparison} /></div>}
                </div>
              );
            })}
          </div>

          {/* Sales value check */}
          {hasDoc && (
            <div data-testid="tally-value-check" className={`rounded-lg border p-4 ${valueMatched ? "border-emerald-200 bg-emerald-50/60" : "border-rose-300 bg-rose-50"}`}>
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-stone-800"><ShieldCheck className="h-4 w-4" /> Sales Bill Value Check</div>
              <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-xs sm:grid-cols-4">
                <div><div className="text-stone-500">Tally Bill Total</div><div className="font-mono font-semibold" data-testid="tally-doc-total">{fmtCurrency(docGrand)}</div></div>
                <div><div className="text-stone-500">System Subtotal</div><div className="font-mono">{fmtCurrency(sysTotals.subtotal)}</div></div>
                <div><div className="text-stone-500">Disc / Tax / Freight</div><div className="font-mono">{fmtCurrency(sysTotals.discount)} / {fmtCurrency(sysTotals.tax)} / {fmtCurrency(sysTotals.freight)}</div></div>
                <div><div className="text-stone-500">System Total</div><div className="font-mono font-semibold" data-testid="tally-sys-total">{fmtCurrency(sysTotals.grandTotal)}</div></div>
              </div>
              <div className="mt-2 border-t border-stone-200 pt-2 text-sm">
                {valueMatched ? <span className="font-semibold text-emerald-700" data-testid="tally-value-ok">✓ Invoice value match</span>
                  : <span className="font-semibold text-rose-700" data-testid="tally-value-mismatch">✕ Value mismatch — difference {fmtCurrency(Math.abs(valueDiff))} (tolerance ₹{tolerance}). Sales bill cannot be booked until corrected.</span>}
              </div>
            </div>
          )}

          {/* Pre-booking validation summary */}
          <div className="rounded-lg border border-stone-200 bg-stone-50/50 p-3 text-xs" data-testid="tally-validation">
            <div className="mb-1 font-semibold text-stone-700">Pre-booking checks</div>
            <ul className="space-y-0.5">
              <li className={buyerResolved ? "text-emerald-700" : "text-rose-600"}>{buyerResolved ? "✓" : "✕"} Buyer resolved{buyerStatus === "MATCHED_GSTIN" ? " (GSTIN)" : buyerStatus === "NEW_BUYER" ? " (new)" : ""}</li>
              <li className={unresolved === 0 ? "text-emerald-700" : "text-amber-700"}>{unresolved === 0 ? "✓" : "⚠"} All product lines resolved {unresolved > 0 && `(${unresolved} pending)`}</li>
              <li className={pendingClass === 0 ? "text-emerald-700" : "text-amber-700"}>{pendingClass === 0 ? "✓" : "⚠"} Classifications complete</li>
              <li className={dateValid ? "text-emerald-700" : "text-rose-600"}>{dateValid ? "✓" : "✕"} Invoice date valid {isBackdated && "· backdated"}</li>
              <li className={valueMatched ? "text-emerald-700" : "text-rose-600"}>{valueMatched ? "✓" : "✕"} Invoice total reconciles</li>
            </ul>
          </div>

          <div className="flex items-center justify-between border-t border-stone-200 pt-3">
            <div className="text-sm"><span className="text-stone-500">System total:&nbsp;</span><span data-testid="tally-total" className="font-mono font-semibold">{fmtCurrency(sysTotals.grandTotal)}</span></div>
            <div className="flex gap-2">
              <Button variant="outline" data-testid="tally-discard" onClick={reset}>Discard</Button>
              <Button data-testid="tally-book" className="gap-2 bg-emerald-600 hover:bg-emerald-700" disabled={!canBook} onClick={() => setConfirmOpen(true)}>
                {booking ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Confirm &amp; Book Sales Bill
              </Button>
            </div>
          </div>
        </div>
      )}

      <CreateProductDialog open={createDlg >= 0} onClose={() => setCreateDlg(-1)} initialName={createDlg >= 0 ? lines[createDlg]?.extractedName : ""} onCreated={(p) => onProductCreated(createDlg, p)} />

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-emerald-600" /> Book operational sales bill</AlertDialogTitle>
            <AlertDialogDescription>Creates a real Sales Bill (source: TALLY_IMPORT) for invoice <strong>{invNo}</strong> dated <strong>{invDate}</strong>{isBackdated ? " (backdated)" : ""}. Rates are captured as buyer prices; current FG stock is NOT deducted. Re-uploading the same invoice is idempotent.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="tally-book-confirm" className="bg-emerald-600 hover:bg-emerald-700" onClick={book}>Yes, book sales bill</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
